import { FC, memo, useCallback, useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';

import Typography from 'app/components/Typography';

import { updateAlertSuppression } from 'app/modules/alerts/core/request';
import { useAlertStore } from 'app/modules/alerts/store';

import { useBoolean, useNumber } from 'app/hooks';

import { AlertItem } from './AlertItem';
import { Alert, AlertType, TAB_ALERTS } from './core/type';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

interface Props {
  type: AlertType;
  index: number;
  alerts: Array<Alert>;
  isShowImg?: boolean;
  tab: TAB_ALERTS;
}

export const AlertsGroup: FC<Props> = memo(({ type, index, alerts, isShowImg = false, tab }) => {
  const alertId = useNumber();

  const isOpen = useBoolean(true);

  const queryClient = useQueryClient();

  const alertSuppressionItems = useAlertStore((s) => s.state.alertSuppressionItems);

  const organizationId = useOrganizationStore((s) => s.state.organization?.id);

  const { mutate: updateAlertSuppressionMutation, isLoading: isLoadingUpdateAlertSuppression } =
    useMutation<void, Error, { relation: string | number; active: boolean }[]>({
      mutationKey: ['update-alert-suppression', type],
      mutationFn: (payload: { relation: string | number; active: boolean }[]) =>
        updateAlertSuppression(type, payload),
      onSuccess: () => {
        queryClient.invalidateQueries(['alertSuppression', type, organizationId]);
      },
    });

  const extractAlertTypeId = (description: string): string | null => {
    const match = description.match(/<b>(.*?)<\/b>/);
    return match ? match[1] : null;
  };

  const suppressionMap = useMemo(() => {
    const map = new Map<string, boolean>();
    alertSuppressionItems?.forEach((item) => {
      const key = tab === TAB_ALERTS.DEVICE_CONNECTION ? item.name : item.sku;
      map.set(key, item.active);
    });
    return map;
  }, [alertSuppressionItems, tab]);

  const handleToggleSuppression = useCallback(
    (alert: Alert) => async () => {
      const matchedSuppression = alertSuppressionItems.find((item) =>
        alert.content?.description?.includes(item.name),
      );

      if (!matchedSuppression) return;

      const payload = {
        relation: matchedSuppression.id,
        active: !matchedSuppression.active,
      };

      alertId.setValue(alert.id);
      updateAlertSuppressionMutation([payload]);
    },
    [alertSuppressionItems, updateAlertSuppressionMutation],
  );

  return (
    <div style={{ marginTop: index === 0 ? 0 : 16 }}>
      <div className={`accordion-item ${isOpen.value ? 'show' : ''}`}>
        <div className="d-flex align-items-center justify-content-between">
          <div
            className="accordion-header"
            id={`kt_accordion_1_header_${index}`}
            style={{
              width: 300,
              minWidth: 300,
              left: 0,
              zIndex: 1,
              cursor: 'default',
            }}
          >
            {/* <ButtonCollapsed
              className={`accordion-button collapsed ${isOpen.value ? 'show' : 0}`}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#kt_accordion_1_body_${index}`}
              aria-expanded={index === 0 ? 'true' : 'false'}
              aria-controls={`kt_accordion_1_body_${index}`}
              style={{ width: 'auto' }}
              onClick={() => {
                toggleShow();
              }}
            >
              <IconArrow
                style={{
                  transform: `rotate(${isOpen.value ? 180 : 0}deg)`,
                }}
              />
              <Typography
                fontSize={18}
                fontWeight={600}
                lineHeight={32}
                color="neutral-10"
                style={{
                  whiteSpace: 'nowrap',
                  marginLeft: 6,
                }}
              >
                {alertTypeText[type as AlertKey] ?? type} ({alerts.length})
              </Typography>
            </ButtonCollapsed> */}
          </div>
        </div>
        {/* <div
          id={`kt_accordion_1_body_${index}`}
          className={`accordion-collapse collapse ${isOpen.value ? 'show' : ''}`}
          aria-labelledby={`kt_accordion_1_header_${index}`}
          data-bs-parent="#kt_accordion_1"
          style={{
            marginTop: isOpen.value ? 12 : 0,
            height: isOpen.value ? 'auto' : 0,
            marginBottom: isOpen.value ? 56 : 0,
          }}
        >
          {alerts.map((alert) => (
            <div
              key={alert.id}
              style={{ marginBottom: 16 }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <AlertItem alert={alert} key={alert.id} isShowImg={isShowImg}></AlertItem>
            </div>
          ))}
        </div> */}

        {alerts.map((alert) => {
          const suppressionKey =
            tab === TAB_ALERTS.DEVICE_CONNECTION
              ? extractAlertTypeId(alert.content.description)
              : alert.content.itemSku;

          // Check if this alert is suppressed
          let suppressed = suppressionKey ? suppressionMap.get(suppressionKey) === true : false;

          // For gateway alerts, check if any hanger sensor in the same gateway is suppressed
          if (!suppressed && suppressionKey && tab === TAB_ALERTS.DEVICE_CONNECTION) {
            // Check if this is a gateway alert by looking for "Including" in the description
            const description = alert.content?.description || '';
            const includingMatch = description.match(/Including\s+(.+)$/);

            if (includingMatch) {
              // Extract hanger sensor names from the description
              const hangerSensorNames = includingMatch[1].split(',').map((name) => name.trim());

              // Check if any of these hanger sensors are suppressed
              suppressed = hangerSensorNames.some((sensorName) => {
                const suppressionItem = alertSuppressionItems.find(
                  (item) => item.name === sensorName,
                );
                return suppressionItem?.active === true;
              });
            }
          }

          return (
            <div
              key={alert.id}
              className="d-flex align-items-center gap-4 mb-4"
              onClick={(e) => e.stopPropagation()}
            >
              <AlertItem alert={alert} isShowImg={isShowImg} />

              <Button
                variant={!suppressed ? 'primary' : 'warning'}
                onClick={handleToggleSuppression(alert)}
                className="d-flex align-items-center justify-content-center gap-2 mw-175px w-100"
                disabled={isLoadingUpdateAlertSuppression}
              >
                {alertId.value === alert.id && isLoadingUpdateAlertSuppression && (
                  <Spinner animation="border" size="sm" className="mb-1" />
                )}
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  lineHeight={22}
                  color={!suppressed ? '#fff' : '#333'}
                >
                  {!suppressed ? 'Click to suppress' : 'Suppressed'}
                </Typography>
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
});
